import React, { useState, useEffect } from "react";
import {
  Drawer,
  Descriptions,
  Tag,
  Button,
  Space,
  Divider,
  Spin,
  Empty,
  Alert,
  Modal,
} from "antd";
import {
  CheckOutlined,
  BellOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { usePickupLists } from "../../../hooks/bot/usePickupLists";
import DistributionsTable from "../../../components/bot/warehouse/DistributionsTable";
import BulkCollectionModal from "./BulkCollectionModal";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const { confirm } = Modal;

const PickupListDetailsDrawer = ({
  visible,
  pickupList,
  onClose,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const { fetchPickupListDetails, markPrepared, notifyParent } =
    usePickupLists();
  const [details, setDetails] = useState(null);
  const [assignmentStatus, setAssignmentStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [distributionModalVisible, setDistributionModalVisible] =
    useState(false);
  const [modalMode, setModalMode] = useState("bulk-collect");

  useEffect(() => {
    if (visible && pickupList) {
      loadDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, pickupList]);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const data = await fetchPickupListDetails(pickupList.id);
      setDetails(data);

      // Fetch the clearance assignment status
      const assignmentResponse = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bot.clearanceAssignmentDetail.replace(
          ":id",
          data.clearance_assignment,
        ),
        null,
        navigate,
      );

      if (assignmentResponse.ok) {
        setAssignmentStatus(assignmentResponse.data.status);
      }
    } catch (error) {
      console.error("Error loading pickup list details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPrepared = () => {
    confirm({
      title: "Mark as Prepared?",
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure all items have been prepared and packed?",
      okText: "Yes, Mark as Prepared",
      cancelText: "Cancel",
      async onOk() {
        const success = await markPrepared(pickupList.id, "");
        if (success) {
          await loadDetails();
          onRefresh();
        }
      },
    });
  };

  const handleNotifyParent = () => {
    confirm({
      title: "Notify Parent?",
      icon: <ExclamationCircleOutlined />,
      content:
        "This will send a notification to the parent that items are ready for pickup.",
      okText: "Yes, Notify",
      cancelText: "Cancel",
      async onOk() {
        const success = await notifyParent(pickupList.id);
        if (success) {
          await loadDetails();
          onRefresh();
        }
      },
    });
  };

  const handleCollectionSuccess = () => {
    loadDetails();
    onRefresh();
  };

  const handleBulkVerify = () => {
    setModalMode("bulk-verify");
    setDistributionModalVisible(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      Pending: "orange",
      Ready: "success",
      "Partially Collected": "processing",
      Completed: "green",
    };
    return colors[status] || "default";
  };

  return (
    <>
      <Drawer
        title="Pickup List Details"
        placement="right"
        width={window.innerWidth > 768 ? 900 : "100%"}
        onClose={onClose}
        open={visible}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
          </div>
        ) : details ? (
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Descriptions bordered column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="List Number" span={2}>
                <strong>{details.list_number}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Student">
                {details.student_name}
              </Descriptions.Item>
              <Descriptions.Item label="Student SPC">
                {details.student_spc}
              </Descriptions.Item>
              <Descriptions.Item label="BOTPC">
                {details.botpc}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(details.status)}>
                  {details.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Generated">
                {dayjs(details.generated_at).format("DD/MM/YYYY HH:mm")}
              </Descriptions.Item>
              <Descriptions.Item label="Pickup Date">
                {details.pickup_date
                  ? dayjs(details.pickup_date).format("DD/MM/YYYY")
                  : "-"}
              </Descriptions.Item>
              {details.prepared_at && (
                <Descriptions.Item label="Prepared At" span={2}>
                  {dayjs(details.prepared_at).format("DD/MM/YYYY HH:mm")} by{" "}
                  {details.prepared_by_name}
                </Descriptions.Item>
              )}
              {details.parent_notified_at && (
                <Descriptions.Item label="Parent Notified" span={2}>
                  {dayjs(details.parent_notified_at).format("DD/MM/YYYY HH:mm")}
                </Descriptions.Item>
              )}
              {details.notes && (
                <Descriptions.Item label="Notes" span={2}>
                  {details.notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Space wrap>
              {details.status === "Pending" && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={handleMarkPrepared}
                >
                  Mark as Prepared
                </Button>
              )}
              {details.status === "Ready" && !details.parent_notified_at && (
                <Button
                  type="default"
                  icon={<BellOutlined />}
                  onClick={handleNotifyParent}
                >
                  Notify Parent
                </Button>
              )}
              {/* Show Collect Items button only if items are prepared AND assignment is Ready for Pickup or Partially Collected */}
              {(details.status === "Ready" ||
                details.status === "Partially Collected") &&
                (assignmentStatus === "Ready for Pickup" ||
                  assignmentStatus === "Partially Collected") && (
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={() => {
                      setModalMode("bulk-collect");
                      setDistributionModalVisible(true);
                    }}
                    style={{ background: "#52c41a" }}
                  >
                    {assignmentStatus === "Partially Collected"
                      ? "Collect Remaining Items"
                      : "Collect Items"}
                  </Button>
                )}

              {/* Show Verify Items button ONLY when status is "Fully Collected" */}
              {assignmentStatus === "Fully Collected" && (
                <Button
                  type="default"
                  icon={<CheckOutlined />}
                  onClick={handleBulkVerify}
                  style={{ borderColor: "#1890ff", color: "#1890ff" }}
                >
                  Verify Items
                </Button>
              )}
            </Space>

            <Divider>Distribution Items</Divider>

            {/* Collection Instructions */}
            {(details.status === "Ready" || details.status === "Notified") && (
              <Alert
                message="Ready for Collection"
                description="Click 'Collect Items' button above to mark items as collected in bulk. Enter collector information once for all selected items."
                type="info"
                showIcon
                style={{ marginBottom: "16px" }}
              />
            )}

            {details.status === "Pending" && (
              <Alert
                message="Items Not Yet Prepared"
                description="Please mark items as prepared before collection can begin. Click 'Mark as Prepared' button above."
                type="info"
                showIcon
                style={{ marginBottom: "16px" }}
              />
            )}

            {assignmentStatus === "Partially Collected" &&
              details.status !== "Pending" && (
                <Alert
                  message="Partial Collection"
                  description="Some items have been collected. Please collect all remaining items before verification."
                  type="warning"
                  showIcon
                  style={{ marginBottom: "16px" }}
                />
              )}

            {assignmentStatus === "Fully Collected" && (
              <Alert
                message="Verification Needed"
                description="All items collected! Click 'Verify Items' button above to verify collected items."
                type="success"
                showIcon
                style={{ marginBottom: "16px" }}
              />
            )}

            {assignmentStatus === "Cleared" && (
              <Alert
                message="Clearance Complete"
                description="All items have been collected and verified. This clearance is complete."
                type="success"
                showIcon
                style={{ marginBottom: "16px" }}
              />
            )}

            {details.distributions && details.distributions.length > 0 ? (
              <DistributionsTable
                distributions={details.distributions}
                loading={loading}
              />
            ) : (
              <Empty description="No distribution items found" />
            )}
          </Space>
        ) : null}
      </Drawer>

      <BulkCollectionModal
        visible={
          distributionModalVisible &&
          (modalMode === "bulk-collect" || modalMode === "bulk-verify")
        }
        distributions={details?.distributions}
        mode={modalMode}
        onClose={() => {
          setDistributionModalVisible(false);
        }}
        onSuccess={handleCollectionSuccess}
      />
    </>
  );
};

export default PickupListDetailsDrawer;
